import type maplibregl from "maplibre-gl";
import type { SpotKind } from "../../shared/mission-quest.ts";
import { mapHasLayer, mapHasStyleObject, whenMapHasStyle } from "../../shared/map-style-safe.ts";
import type { LngLat } from "../../shared/types.ts";
import { SPOT_THUMB_MARKER_PX } from "../../shared/spot-thumbnail.ts";

export type QuestSpotMapKind = "start" | "finish" | "spot";

/** routeOrdinal: S/F/numbers on colored circles. spotTitleLabel: title pill only. spotThumbnail: photo circle + title. */
export type QuestSpotPinDisplay = "routeOrdinal" | "spotTitleLabel" | "spotThumbnail";

export interface QuestSpotMapPin {
  lng: number;
  lat: number;
  kind: QuestSpotMapKind;
  /** Label sprite for routeOrdinal (S, F, or index). */
  label: string;
  index?: number;
  placing?: boolean;
  /** Stable sprite id for spotThumbnail mode. */
  spriteKey?: string;
  title?: string | null;
  thumbnailUrl?: string | null;
}

export interface QuestSpotMapLayerIds {
  source: string;
  circle: string;
  label: string;
}

export interface QuestSpotMapLayerOptions {
  /** Circle radius in px (22px pin ≈ 11). Route S/F pins use 12. */
  circleRadius?: number;
  display?: QuestSpotPinDisplay;
}

export const HOME_QUEST_SPOT_LAYER_IDS = questSpotMapLayerIds("home-quest-spots");
export const ROUTE_MAP_PIN_LAYER_IDS = questSpotMapLayerIds("route-map-pins");
export const QUEST_MAP_SPOT_LAYER_IDS = questSpotMapLayerIds("quest-map-spots");

export const HOME_QUEST_HIGHLIGHT_LAYER_IDS = {
  source: "home-quest-highlight-src",
  circle: "home-quest-highlight-circle",
} as const;

const emptyCollection = () => ({ type: "FeatureCollection" as const, features: [] });

export const QUEST_SPOT_LABEL_IMAGE_PREFIX = "mapus-quest-spot-lbl-";
export const QUEST_SPOT_TITLE_IMAGE_PREFIX = "mapus-quest-spot-ttl-";
export const QUEST_SPOT_MARKER_IMAGE_PREFIX = "mapus-quest-spot-mkr-";

const labelImagesByMap = new WeakMap<maplibregl.Map, Set<string>>();
const markerImagesByMap = new WeakMap<maplibregl.Map, Set<string>>();
const markerLoadsByMap = new WeakMap<maplibregl.Map, Map<string, Promise<void>>>();
/** Sprite keys whose marker canvas includes a loaded checkpoint photo (not the pin fallback). */
const markerThumbLoadedByMap = new WeakMap<maplibregl.Map, Set<string>>();

export function questSpotMarkerThumbnailReady(map: maplibregl.Map, spriteKey: string): boolean {
  return markerThumbLoadedByMap.get(map)?.has(spriteKey) ?? false;
}

function markerImageRegistered(map: maplibregl.Map, id: string): boolean {
  try {
    return map.hasImage(id);
  } catch {
    return false;
  }
}

/** Drop stale “registered” flags when MapLibre lost images (style rebuild) so sprites repopulate. */
function markerSpriteNeedsRegister(map: maplibregl.Map, known: Set<string>, id: string): boolean {
  if (!known.has(id)) return true;
  return !markerImageRegistered(map, id);
}

const THUMB_PX = SPOT_THUMB_MARKER_PX;
const MARKER_H = 44;
const MARKER_PAD = 8;
const MARKER_SPRITE_PIXEL_RATIO = 1;
const FALLBACK_ICON = "📍";

/** Max photo+title marker width in px (sprite canvas). */
export const QUEST_SPOT_MARKER_MAX_WIDTH_PX = THUMB_PX + MARKER_PAD + 160 + MARKER_PAD;
/** Map fit inset for thumbnail markers: photo size + border, not full title width (avoids city-level zoom). */
export const QUEST_SPOT_MARKER_FIT_PADDING_PX = Math.ceil(THUMB_PX / 2) + 12;

export function questSpotMapLayerIds(prefix: string): QuestSpotMapLayerIds {
  return {
    source: `${prefix}-spot-src`,
    circle: `${prefix}-spot-circle`,
    label: `${prefix}-spot-label`,
  };
}

export function questSpotMapSelectionLayerIds(ids: QuestSpotMapLayerIds): { source: string; circle: string } {
  return {
    source: `${ids.source}-sel`,
    circle: `${ids.circle}-sel`,
  };
}

export function spotKindFromRaw(kind: SpotKind | null | undefined): QuestSpotMapKind {
  return kind === "start" || kind === "finish" ? kind : "spot";
}

export function spotPinLabel(kind: QuestSpotMapKind, spotIndex: number): string {
  if (kind === "start") return "S";
  if (kind === "finish") return "F";
  return String(spotIndex + 1);
}

export function questSpotSpriteKey(index: number, title: string | null | undefined, thumbnailUrl: string | null | undefined): string {
  const t = (title ?? "").trim().slice(0, 40);
  const u = thumbnailUrl ?? "";
  let h = 0;
  const s = `${index}|${t}|${u}`;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return `${index}-${(h >>> 0).toString(36)}`;
}

export function questSpotMapPinForSpotTrace(
  spot: { lng: number; lat: number; title?: string | null; thumbnail_image?: string | null },
  index: number,
): QuestSpotMapPin {
  const title = spot.title?.trim() || "";
  const thumbnailUrl = spot.thumbnail_image ?? null;
  return {
    lng: spot.lng,
    lat: spot.lat,
    kind: "spot",
    label: "",
    index,
    spriteKey: questSpotSpriteKey(index, title, thumbnailUrl),
    title: title || null,
    thumbnailUrl,
  };
}

const circleColorExpr = [
  "case",
  ["boolean", ["get", "placing"], false],
  "#9a9a9a",
  ["match", ["get", "kind"], "start", "#1f9d55", "finish", "#111", "#8b5cf6"],
] as maplibregl.ExpressionSpecification;

function circlePaint(radius: number): maplibregl.CircleLayerSpecification["paint"] {
  return {
    "circle-radius": radius,
    "circle-color": circleColorExpr,
    "circle-stroke-color": "#ffffff",
    "circle-stroke-width": 2,
  };
}

const routeLabelIconLayout: maplibregl.SymbolLayerSpecification["layout"] = {
  "icon-image": ["concat", QUEST_SPOT_LABEL_IMAGE_PREFIX, ["get", "label"]],
  "icon-size": 1,
  "icon-allow-overlap": true,
  "icon-ignore-placement": true,
};

const spotMarkerIconLayout: maplibregl.SymbolLayerSpecification["layout"] = {
  "icon-image": ["concat", QUEST_SPOT_MARKER_IMAGE_PREFIX, ["get", "spriteKey"]],
  "icon-size": 1,
  "icon-anchor": "left",
  "icon-offset": [THUMB_PX / 2, 0],
  "icon-allow-overlap": true,
  "icon-ignore-placement": true,
};

const spotTitleIconLayout: maplibregl.SymbolLayerSpecification["layout"] = {
  "icon-image": ["concat", QUEST_SPOT_TITLE_IMAGE_PREFIX, ["get", "spriteKey"]],
  "icon-size": 1,
  "icon-anchor": "left",
  "icon-offset": [THUMB_PX / 2, 0],
  "icon-allow-overlap": true,
  "icon-ignore-placement": true,
};

export function ensureQuestSpotLabelImage(map: maplibregl.Map, label: string): void {
  if (!label || typeof document === "undefined") return;
  const id = `${QUEST_SPOT_LABEL_IMAGE_PREFIX}${label}`;
  let known = labelImagesByMap.get(map);
  if (!known) {
    known = new Set();
    labelImagesByMap.set(map, known);
  }
  if (!markerSpriteNeedsRegister(map, known, id)) return;
  known.delete(id);
  const size = 32;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, size, size);
  ctx.font = "bold 13px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#ffffff";
  ctx.fillText(label, size / 2, size / 2 + 0.5);
  const imageData = ctx.getImageData(0, 0, size, size);
  try {
    map.addImage(id, { width: size, height: size, data: imageData.data }, { pixelRatio: 2 });
    known.add(id);
  } catch {
    /* duplicate or style swap */
  }
}

function drawDefaultPin(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number): void {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy - r * 0.15, r, 0, Math.PI * 2);
  ctx.fillStyle = "#8b5cf6";
  ctx.fill();
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy - r * 0.15, r * 0.38, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.restore();
}

function drawThumbnailCircle(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, img: CanvasImageSource | null): void {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  if (img) {
    ctx.drawImage(img, cx - r, cy - r, r * 2, r * 2);
  } else {
    ctx.fillStyle = "#e8e4f8";
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
    ctx.font = "22px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(FALLBACK_ICON, cx, cy + 1);
  }
  ctx.restore();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 3;
  ctx.stroke();
}

function registerMarkerCanvas(map: maplibregl.Map, id: string, canvas: HTMLCanvasElement, known: Set<string>): boolean {
  const imageData = canvas.getContext("2d")!.getImageData(0, 0, canvas.width, canvas.height);
  const ratio = MARKER_SPRITE_PIXEL_RATIO;
  try {
    map.addImage(id, { width: canvas.width, height: canvas.height, data: imageData.data }, { pixelRatio: ratio });
    known.add(id);
  } catch {
    try {
      map.updateImage(id, { width: canvas.width, height: canvas.height, data: imageData.data });
      known.add(id);
    } catch {
      return false;
    }
  }
  try {
    map.triggerRepaint();
  } catch {
    /* torn down */
  }
  return markerImageRegistered(map, id);
}

function renderSpotMarkerSprite(
  map: maplibregl.Map,
  pin: QuestSpotMapPin,
  img: CanvasImageSource | null,
): void {
  if (typeof document === "undefined") return;
  const spriteKey = pin.spriteKey ?? questSpotSpriteKey(pin.index ?? 0, pin.title, pin.thumbnailUrl);
  const id = `${QUEST_SPOT_MARKER_IMAGE_PREFIX}${spriteKey}`;
  let known = markerImagesByMap.get(map);
  if (!known) {
    known = new Set();
    markerImagesByMap.set(map, known);
  }
  let thumbLoaded = markerThumbLoadedByMap.get(map);
  if (!thumbLoaded) {
    thumbLoaded = new Set();
    markerThumbLoadedByMap.set(map, thumbLoaded);
  }
  const title = (pin.title ?? "").trim() || "…";
  const ctxMeasure = document.createElement("canvas").getContext("2d");
  if (!ctxMeasure) return;
  ctxMeasure.font = "600 12px system-ui, -apple-system, Segoe UI, sans-serif";
  const textW = Math.min(160, Math.ceil(ctxMeasure.measureText(title).width));
  const w = THUMB_PX + MARKER_PAD + textW + MARKER_PAD;
  const h = MARKER_H;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, w, h);
  const cy = h / 2;
  const cx = THUMB_PX / 2 + 1;
  const thumbR = THUMB_PX / 2 - 1;
  if (img || pin.thumbnailUrl?.trim()) {
    drawThumbnailCircle(ctx, cx, cy, thumbR, img);
  } else {
    drawDefaultPin(ctx, cx, cy, thumbR);
  }
  ctx.font = "600 12px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#111111";
  ctx.fillText(title, THUMB_PX + MARKER_PAD, cy + 0.5);
  const registered = registerMarkerCanvas(map, id, canvas, known);
  if (img && registered) thumbLoaded.add(spriteKey);
  else thumbLoaded.delete(spriteKey);
}

function renderSpotTitleLabelSprite(map: maplibregl.Map, pin: QuestSpotMapPin): void {
  if (typeof document === "undefined") return;
  const spriteKey = pin.spriteKey ?? questSpotSpriteKey(pin.index ?? 0, pin.title, pin.thumbnailUrl);
  const id = `${QUEST_SPOT_TITLE_IMAGE_PREFIX}${spriteKey}`;
  let known = markerImagesByMap.get(map);
  if (!known) {
    known = new Set();
    markerImagesByMap.set(map, known);
  }
  const title = (pin.title ?? "").trim() || "…";
  const ctxMeasure = document.createElement("canvas").getContext("2d");
  if (!ctxMeasure) return;
  ctxMeasure.font = "600 12px system-ui, -apple-system, Segoe UI, sans-serif";
  const textW = Math.min(168, Math.ceil(ctxMeasure.measureText(title).width));
  const dot = 8;
  const pad = 6;
  const h = MARKER_H;
  const w = pad + dot + pad + textW + pad;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, w, h);
  const cy = h / 2;
  ctx.fillStyle = "rgba(255,255,255,0.94)";
  ctx.strokeStyle = "#d8d4cc";
  ctx.lineWidth = 1;
  roundRect(ctx, 0.5, 0.5, w - 1, h - 1, 6);
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(pad + dot / 2, cy, dot / 2 - 0.5, 0, Math.PI * 2);
  ctx.fillStyle = "#8b5cf6";
  ctx.fill();
  ctx.font = "600 12px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#111111";
  ctx.fillText(title, pad + dot + pad, cy + 0.5);
  registerMarkerCanvas(map, id, canvas, known);
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

export function ensureQuestSpotTitleLabelSprite(map: maplibregl.Map, pin: QuestSpotMapPin): void {
  if (typeof document === "undefined") return;
  const spriteKey = pin.spriteKey ?? questSpotSpriteKey(pin.index ?? 0, pin.title, pin.thumbnailUrl);
  pin.spriteKey = spriteKey;
  const id = `${QUEST_SPOT_TITLE_IMAGE_PREFIX}${spriteKey}`;
  let known = markerImagesByMap.get(map);
  if (!known) {
    known = new Set();
    markerImagesByMap.set(map, known);
  }
  if (!markerSpriteNeedsRegister(map, known, id)) return;
  known.delete(id);
  renderSpotTitleLabelSprite(map, pin);
}

async function decodeMarkerImageBlob(blob: Blob, contentType: string): Promise<CanvasImageSource | null> {
  const mime = (blob.type || contentType.split(";")[0]?.trim() || "").toLowerCase();
  if (!mime.startsWith("image/")) return null;
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(blob);
    } catch {
      /* fall through */
    }
  }
  const objectUrl = URL.createObjectURL(blob);
  return await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };
    img.src = objectUrl;
  });
}

async function loadMarkerImage(url: string): Promise<CanvasImageSource | null> {
  const trimmed = url.trim();
  if (!trimmed || typeof fetch === "undefined") return null;
  try {
    const res = await fetch(trimmed, { credentials: "same-origin", cache: "no-store" });
    if (!res.ok) return null;
    const contentType = res.headers.get("content-type") ?? "";
    if (!contentType.startsWith("image/")) return null;
    const blob = await res.blob();
    return decodeMarkerImageBlob(blob, contentType);
  } catch {
    return null;
  }
}

export function ensureQuestSpotMarkerSprite(map: maplibregl.Map, pin: QuestSpotMapPin): void {
  if (typeof document === "undefined") return;
  const spriteKey = pin.spriteKey ?? questSpotSpriteKey(pin.index ?? 0, pin.title, pin.thumbnailUrl);
  pin.spriteKey = spriteKey;
  const id = `${QUEST_SPOT_MARKER_IMAGE_PREFIX}${spriteKey}`;
  let known = markerImagesByMap.get(map);
  if (!known) {
    known = new Set();
    markerImagesByMap.set(map, known);
  }
  const url = pin.thumbnailUrl?.trim() ?? "";
  const thumbReady = questSpotMarkerThumbnailReady(map, spriteKey);
  const hasSprite = markerImageRegistered(map, id);
  if (markerSpriteNeedsRegister(map, known, id)) {
    known.delete(id);
    renderSpotMarkerSprite(map, pin, null);
  } else if (!url) {
    return;
  } else if (thumbReady && hasSprite) {
    return;
  }
  let loads = markerLoadsByMap.get(map);
  if (!loads) {
    loads = new Map();
    markerLoadsByMap.set(map, loads);
  }
  if (loads.has(spriteKey)) return;
  const job = loadMarkerImage(url)
    .then((img) => {
      renderSpotMarkerSprite(map, pin, img);
    })
    .finally(() => {
      loads?.delete(spriteKey);
    });
  loads.set(spriteKey, job);
}

export function ensureQuestSpotMapLayers(
  map: maplibregl.Map,
  ids: QuestSpotMapLayerIds,
  opts: QuestSpotMapLayerOptions = {},
): void {
  if (!mapHasStyleObject(map)) return;
  const radius = opts.circleRadius ?? 11;
  const display = opts.display ?? "routeOrdinal";
  if (!map.getSource(ids.source)) {
    map.addSource(ids.source, { type: "geojson", data: emptyCollection() });
  }
  if (!mapHasLayer(map, ids.circle)) {
    map.addLayer({
      id: ids.circle,
      type: "circle",
      source: ids.source,
      paint: circlePaint(radius),
    });
  }
  if (!mapHasLayer(map, ids.label)) {
    const layout =
      display === "spotThumbnail"
        ? spotMarkerIconLayout
        : display === "spotTitleLabel"
          ? spotTitleIconLayout
          : routeLabelIconLayout;
    map.addLayer({
      id: ids.label,
      type: "symbol",
      source: ids.source,
      layout,
    });
  } else {
    const iconImage =
      display === "spotThumbnail"
        ? (["concat", QUEST_SPOT_MARKER_IMAGE_PREFIX, ["get", "spriteKey"]] as maplibregl.ExpressionSpecification)
        : display === "spotTitleLabel"
          ? (["concat", QUEST_SPOT_TITLE_IMAGE_PREFIX, ["get", "spriteKey"]] as maplibregl.ExpressionSpecification)
          : (["concat", QUEST_SPOT_LABEL_IMAGE_PREFIX, ["get", "label"]] as maplibregl.ExpressionSpecification);
    map.setLayoutProperty(ids.label, "icon-image", iconImage);
    map.setLayoutProperty(
      ids.label,
      "icon-anchor",
      display === "spotThumbnail" || display === "spotTitleLabel" ? "left" : "center",
    );
  }
  const circleVis = display === "spotThumbnail" || display === "spotTitleLabel" ? "none" : "visible";
  try {
    map.setLayoutProperty(ids.circle, "visibility", circleVis);
  } catch {
    /* mid-add */
  }
}

export function setQuestSpotMapPins(
  map: maplibregl.Map,
  ids: QuestSpotMapLayerIds,
  pins: readonly QuestSpotMapPin[],
  opts: QuestSpotMapLayerOptions = {},
): void {
  whenMapHasStyle(map, () => {
    try {
      const display = opts.display ?? "routeOrdinal";
      ensureQuestSpotMapLayers(map, ids, opts);
      const filtered = pins.filter((p) => Number.isFinite(p.lng) && Number.isFinite(p.lat));
      for (const p of filtered) {
        if (display === "spotThumbnail") ensureQuestSpotMarkerSprite(map, p);
        else if (display === "spotTitleLabel") ensureQuestSpotTitleLabelSprite(map, p);
        else ensureQuestSpotLabelImage(map, p.label);
      }
      const src = map.getSource(ids.source) as maplibregl.GeoJSONSource | undefined;
      if (!src) return;
      src.setData({
        type: "FeatureCollection",
        features: filtered.map((p, i) => ({
          type: "Feature" as const,
          properties: {
            kind: p.kind,
            label: p.label,
            spriteKey: p.spriteKey ?? questSpotSpriteKey(p.index ?? i, p.title, p.thumbnailUrl),
            index: p.index ?? i,
            placing: p.placing === true,
          },
          geometry: { type: "Point" as const, coordinates: [p.lng, p.lat] as LngLat },
        })),
      });
      moveQuestSpotMapLayersOnTop(map, ids);
      const circleVis = display === "spotThumbnail" || display === "spotTitleLabel" ? "none" : "visible";
      try {
        map.setLayoutProperty(ids.circle, "visibility", circleVis);
      } catch {
        /* mid-teardown */
      }
    } catch {
      /* style swap or torn-down map */
    }
  });
}

export interface QuestSpotMapClearOptions {
  /** Hide MapLibre circle/symbol layers (home map uses DOM photo markers only). */
  hideLayers?: boolean;
}

export function clearQuestSpotMapPins(
  map: maplibregl.Map,
  ids: QuestSpotMapLayerIds,
  opts: QuestSpotMapClearOptions = {},
): void {
  setQuestSpotMapPins(map, ids, []);
  setQuestSpotMapSelection(map, ids, null);
  if (!opts.hideLayers) return;
  whenMapHasStyle(map, () => {
    try {
      if (mapHasLayer(map, ids.circle)) map.setLayoutProperty(ids.circle, "visibility", "none");
      if (mapHasLayer(map, ids.label)) map.setLayoutProperty(ids.label, "visibility", "none");
    } catch {
      /* style torn down */
    }
  });
}

/** Pulse ring under the selected spot marker on the quest map. */
export function setQuestSpotMapSelection(
  map: maplibregl.Map,
  ids: QuestSpotMapLayerIds,
  pin: Pick<QuestSpotMapPin, "lng" | "lat"> | null,
): void {
  whenMapHasStyle(map, () => {
    try {
      const selIds = questSpotMapSelectionLayerIds(ids);
      if (!map.getSource(selIds.source)) {
        map.addSource(selIds.source, { type: "geojson", data: emptyCollection() });
      }
      if (!mapHasLayer(map, selIds.circle)) {
        map.addLayer({
          id: selIds.circle,
          type: "circle",
          source: selIds.source,
          paint: {
            "circle-radius": 22,
            "circle-color": "#8b5cf6",
            "circle-opacity": 0.22,
            "circle-stroke-color": "#8b5cf6",
            "circle-stroke-width": 3,
            "circle-stroke-opacity": 0.95,
          },
        });
      }
      const src = map.getSource(selIds.source) as maplibregl.GeoJSONSource | undefined;
      if (!src) return;
      if (!pin || !Number.isFinite(pin.lng) || !Number.isFinite(pin.lat)) {
        src.setData(emptyCollection());
        try {
          map.setLayoutProperty(selIds.circle, "visibility", "none");
        } catch {
          /* mid-teardown */
        }
        return;
      }
      try {
        map.setLayoutProperty(selIds.circle, "visibility", "visible");
      } catch {
        /* mid-teardown */
      }
      src.setData({
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: {},
            geometry: { type: "Point", coordinates: [pin.lng, pin.lat] },
          },
        ],
      });
      if (mapHasLayer(map, selIds.circle)) map.moveLayer(selIds.circle);
    } catch {
      /* style swap */
    }
  });
}

export function moveQuestSpotMapLayersOnTop(map: maplibregl.Map, ids: QuestSpotMapLayerIds): void {
  try {
    if (mapHasLayer(map, ids.circle)) map.moveLayer(ids.circle);
    if (mapHasLayer(map, ids.label)) map.moveLayer(ids.label);
  } catch {
    /* map mid-teardown */
  }
}

export function ensureHomeQuestHighlightLayer(map: maplibregl.Map): void {
  if (!mapHasStyleObject(map)) return;
  if (!map.getSource(HOME_QUEST_HIGHLIGHT_LAYER_IDS.source)) {
    map.addSource(HOME_QUEST_HIGHLIGHT_LAYER_IDS.source, { type: "geojson", data: emptyCollection() });
  }
  if (!mapHasLayer(map, HOME_QUEST_HIGHLIGHT_LAYER_IDS.circle)) {
    map.addLayer({
      id: HOME_QUEST_HIGHLIGHT_LAYER_IDS.circle,
      type: "circle",
      source: HOME_QUEST_HIGHLIGHT_LAYER_IDS.source,
      paint: {
        "circle-radius": 14,
        "circle-color": "#ff6b4a",
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 3,
        "circle-opacity": 0.95,
      },
    });
  }
}

export function setHomeQuestHighlight(map: maplibregl.Map, at: LngLat | null): void {
  whenMapHasStyle(map, () => {
    try {
      ensureHomeQuestHighlightLayer(map);
      const src = map.getSource(HOME_QUEST_HIGHLIGHT_LAYER_IDS.source) as maplibregl.GeoJSONSource | undefined;
      if (!src) return;
      if (!at || !Number.isFinite(at[0]) || !Number.isFinite(at[1])) {
        src.setData(emptyCollection());
        return;
      }
      src.setData({
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: {},
            geometry: { type: "Point", coordinates: at },
          },
        ],
      });
      if (mapHasLayer(map, HOME_QUEST_HIGHLIGHT_LAYER_IDS.circle)) {
        map.moveLayer(HOME_QUEST_HIGHLIGHT_LAYER_IDS.circle);
      }
    } catch {
      /* style swap */
    }
  });
}
